/** Renders the recurring-only Activity fields shared by create, edit, and conversion. */
import { DIFFICULTIES, PERIODS, type Difficulty, type Period } from '@life-rpg/shared';
import React, { type Dispatch, type SetStateAction } from 'react';

import { titleCase } from './activityUi.js';
import {
  formatDifficultyPeriod,
  type RecurringActivityFormState,
} from './recurringActivityForm.js';

interface RecurringActivityFieldsProps {
  form: RecurringActivityFormState;
  setForm: Dispatch<SetStateAction<RecurringActivityFormState>>;
}

export const RecurringActivityFields = ({
  form,
  setForm,
}: RecurringActivityFieldsProps): React.JSX.Element => {
  const setField = <Key extends keyof RecurringActivityFormState>(
    field: Key,
    value: RecurringActivityFormState[Key],
  ): void => setForm((current) => ({ ...current, [field]: value }));
  const setQuickAdd = (index: number, value: string): void =>
    setForm((current) => ({
      ...current,
      quickAdd: current.quickAdd.map((amount, amountIndex) =>
        amountIndex === index ? value : amount,
      ),
    }));

  return (
    <>
      <label>
        Name
        <input
          required
          value={form.name}
          onChange={(event) => setField('name', event.target.value)}
        />
      </label>
      <label>
        Unit label
        <input
          required
          value={form.unitLabel}
          onChange={(event) => setField('unitLabel', event.target.value)}
        />
      </label>
      <fieldset className="quick-add-editor">
        <legend>Quick-add amounts</legend>
        {form.quickAdd.map((amount, index) => (
          <div className="quick-add-editor-row" key={index}>
            <label>
              Quick-add amount {index + 1}
              <input
                required
                min="1"
                step="1"
                type="number"
                value={amount}
                onChange={(event) => setQuickAdd(index, event.target.value)}
              />
            </label>
            <button
              aria-label={`Remove quick-add amount ${index + 1}`}
              className="secondary"
              disabled={form.quickAdd.length === 1}
              type="button"
              onClick={() =>
                setForm((current) => ({
                  ...current,
                  quickAdd: current.quickAdd.filter(
                    (_amount, amountIndex) => amountIndex !== index,
                  ),
                }))
              }
            >
              Remove
            </button>
          </div>
        ))}
        <button
          className="secondary"
          type="button"
          onClick={() =>
            setForm((current) => ({ ...current, quickAdd: [...current.quickAdd, ''] }))
          }
        >
          Add quick-add amount
        </button>
      </fieldset>
      <label>
        Target quantity
        <input
          required
          min="1"
          step="1"
          type="number"
          value={form.targetQty}
          onChange={(event) => setField('targetQty', event.target.value)}
        />
      </label>
      <label>
        Repeat every
        <input
          required
          min="1"
          step="1"
          type="number"
          value={form.targetEvery}
          onChange={(event) => setField('targetEvery', event.target.value)}
        />
      </label>
      <label>
        Target period
        <select
          value={form.targetPer}
          onChange={(event) => setField('targetPer', event.target.value as Period)}
        >
          {PERIODS.map((period) => (
            <option key={period} value={period}>
              {titleCase(period)}
            </option>
          ))}
        </select>
      </label>
      {form.targetPer === 'month' ? (
        <>
          <label>
            Starting month
            <input
              required
              type="month"
              value={form.targetStartsOn.slice(0, 7)}
              onChange={(event) => setField('targetStartsOn', `${event.target.value}-01`)}
            />
          </label>
          <label>
            Day of month
            <input
              required
              max="31"
              min="1"
              step="1"
              type="number"
              value={form.targetDayOfMonth}
              onChange={(event) => setField('targetDayOfMonth', event.target.value)}
            />
          </label>
        </>
      ) : (
        <label>
          Period starts
          <input
            required
            type="date"
            value={form.targetStartsOn}
            onChange={(event) => setField('targetStartsOn', event.target.value)}
          />
        </label>
      )}
      <label>
        How hard is it to hit this target for one{' '}
        {formatDifficultyPeriod(form.targetEvery, form.targetPer)}?
        <select
          aria-label="Difficulty"
          value={form.difficulty}
          onChange={(event) => setField('difficulty', event.target.value as Difficulty)}
        >
          {DIFFICULTIES.map((option) => (
            <option key={option} value={option}>
              {titleCase(option)}
            </option>
          ))}
        </select>
      </label>
    </>
  );
};
